import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const DEFAULT_LOGO = 'https://storage.readdy-site.link/project_files/298b1d8a-faaf-4807-9ba8-44d9fc68ed7b/eaf59820-67d5-4ccb-91a0-f3d787c17c83_Logo---Empresa.png?v=30d6f24b038736afd047b4d89d332490';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);
  const [logoReady, setLogoReady] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const carregarLogo = async () => {
      try {
        const { data, error } = await supabase
          .from('configuracoes_sistema')
          .select('valor')
          .eq('chave', 'logo_url')
          .maybeSingle();
        if (error) throw error;
        if (data?.valor) {
          // Pré-carrega a imagem antes de mostrar para evitar flash
          const img = new Image();
          img.onload = () => {
            setLogoUrl(data.valor);
            setLogoReady(true);
          };
          img.onerror = () => setLogoReady(true);
          img.src = data.valor;
        } else {
          setLogoReady(true);
        }
      } catch (err) {
        setLogoReady(true);
      }
    };
    carregarLogo();
  }, []);

  // ... existing code ...

  const navLinks = [
    { name: 'Início', path: '/' },
    { name: 'Produtos', path: '/produtos' },
    { name: 'Sobre', path: '/sobre' },
    { name: 'Afiliados', path: '/afiliados' },
    { name: 'Equipa', path: '/equipa' },
    { name: 'Contactos', path: '/contactos' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-luxury-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav className="px-8 lg:px-20 py-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <div className="w-12 h-12 bg-gradient-gold rounded-lg flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 overflow-hidden">
              <img 
                src={logoReady ? logoUrl : DEFAULT_LOGO}
                alt="Dindimcast Logo" 
                className="w-full h-full object-contain p-1"
                onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_LOGO; }}
              />
            </div>
            <span className="text-white text-2xl font-serif font-bold">
              Dindimcast
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-colors duration-300 cursor-pointer ${
                  location.pathname === link.path
                    ? 'text-luxury-gold'
                    : 'text-gray-300 hover:text-luxury-gold'
                }`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Auth Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <Link
                to="/area-cliente"
                className="px-6 py-2.5 bg-gradient-gold text-black font-semibold rounded-full hover:shadow-gold transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-user-line mr-2"></i>
                Minha Conta
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-6 py-2.5 text-luxury-gold border-2 border-luxury-gold rounded-full hover:bg-luxury-gold hover:text-black font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Entrar
                </Link>
                <Link
                  to="/login"
                  className="px-6 py-2.5 bg-gradient-gold text-black font-semibold rounded-full hover:shadow-gold transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  Registar
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-6 pb-6 border-t border-luxury-gold/20"
            >
              <div className="flex flex-col gap-4 mt-6">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-base font-medium transition-colors duration-300 cursor-pointer ${
                      location.pathname === link.path
                        ? 'text-luxury-gold'
                        : 'text-gray-300 hover:text-luxury-gold'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="flex flex-col gap-3 mt-4">
                  {user ? (
                    <Link
                      to="/area-cliente"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-6 py-2.5 bg-gradient-gold text-black font-semibold rounded-full hover:shadow-gold transition-all duration-300 text-center cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-user-line mr-2"></i>
                      Minha Conta
                    </Link>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="px-6 py-2.5 text-luxury-gold border-2 border-luxury-gold rounded-full hover:bg-luxury-gold hover:text-black font-semibold transition-all duration-300 text-center cursor-pointer whitespace-nowrap"
                      >
                        Entrar
                      </Link>
                      <Link
                        to="/login"
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="px-6 py-2.5 bg-gradient-gold text-black font-semibold rounded-full hover:shadow-gold transition-all duration-300 text-center cursor-pointer whitespace-nowrap"
                      >
                        Registar
                      </Link>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
